import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import mealApi from "../../../api/meal/mealApi";
import type {
  MealConfigurationData,
  MealMonitoringData,
  MealMonitoringMeal,
  MealMonitoringPayload,
} from "../../../api/meal/types/mealMonitoringType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";

interface MealMonitoringState {
  data: MealMonitoringData | null;
  loading: boolean;
  error: string | null;
  requestId: string | null;
  confirmLoading: boolean;
  confirmError: string | null;
  confirmRequestId: string | null;
}

const initialState: MealMonitoringState = {
  data: null,
  loading: false,
  error: null,
  requestId: null,
  confirmLoading: false,
  confirmError: null,
  confirmRequestId: null,
};

export const fetchMealMonitoring = createAsyncThunk<
  MealMonitoringData,
  MealMonitoringPayload,
  { rejectValue: RejectPayload }
>("mealMonitoring/fetch", async (payload, { rejectWithValue, signal }) => {
  try {
    const response = await mealApi.getMealMonitoring(payload, signal);
    return response.data;
  } catch (err) {
    if (isRequestCancelled(err, signal)) throw err;

    return rejectWithValue(
      toRejectPayload(err, "Gagal memuat data meal monitoring"),
    );
  }
});

export const confirmMealOrder = createAsyncThunk<
  MealConfigurationData,
  string,
  { rejectValue: RejectPayload }
>("mealMonitoring/confirmOrder", async (uuid, { rejectWithValue, signal }) => {
  try {
    const response = await mealApi.mealConfirmOrder(uuid, signal);
    return response.data;
  } catch (err) {
    if (isRequestCancelled(err, signal)) throw err;

    return rejectWithValue(
      toRejectPayload(err, "Gagal melakukan confirm order"),
    );
  }
});

const mealMonitoringSlice = createSlice({
  name: "mealMonitoring",
  initialState,
  reducers: {
    resetMealMonitoring: () => initialState,
    clearMealMonitoringActionError: (state) => {
      state.confirmError = null;
    },
    patchMealMonitoringMeal: (
      state,
      action: PayloadAction<Partial<MealMonitoringMeal>>,
    ) => {
      if (state.data) {
        state.data.meal = {
          ...state.data.meal,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMealMonitoring.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchMealMonitoring.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.requestId = null;
      })
      .addCase(fetchMealMonitoring.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.loading = false;
        state.requestId = null;

        if (action.meta.aborted) {
          state.error = null;
          return;
        }

        state.error =
          action.payload?.message ?? "Gagal memuat data meal monitoring";
      })
      .addCase(confirmMealOrder.pending, (state, action) => {
        state.confirmLoading = true;
        state.confirmError = null;
        state.confirmRequestId = action.meta.requestId;
      })
      .addCase(confirmMealOrder.fulfilled, (state, action) => {
        if (state.confirmRequestId !== action.meta.requestId) return;

        state.confirmLoading = false;
        state.confirmError = null;
        state.confirmRequestId = null;

        if (state.data) {
          state.data.meal = {
            ...state.data.meal,
            ...action.payload,
          };
        }
      })
      .addCase(confirmMealOrder.rejected, (state, action) => {
        if (state.confirmRequestId !== action.meta.requestId) return;

        state.confirmLoading = false;
        state.confirmRequestId = null;

        if (action.meta.aborted) {
          state.confirmError = null;
          return;
        }

        state.confirmError =
          action.payload?.message ?? "Gagal melakukan confirm order";
      });
  },
});

export const {
  resetMealMonitoring,
  clearMealMonitoringActionError,
  patchMealMonitoringMeal,
} = mealMonitoringSlice.actions;

export default mealMonitoringSlice.reducer;
